// src/pages/ActivitiesBuilder/components/ActivitySearch.tsx
import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Chip,
  IconButton,
  Rating,
  Divider,
  InputAdornment,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  OpenInNew as OpenInNewIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon
} from '@mui/icons-material';

import { Trip } from '../../../contexts/ItineraryContext';
import { useActivity, Activity } from '../../../contexts/ActivityContext';
import ActivityDetailDialog from './ActivityDetailDialog';

interface ActivitySearchProps {
  trip: Trip;
  onSuccess: (message: string) => void;
}

const ActivitySearch: React.FC<ActivitySearchProps> = ({ trip, onSuccess }) => {
  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Activity[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [filters, setFilters] = useState({
    type: 'all',
    minPrice: 0,
    maxPrice: 1000,
    rating: 0,
  });

  // Get activities context
  const { 
    searchActivities, 
    saveActivity, 
    unsaveActivity
  } = useActivity();

  // Handle search
  const handleSearch = async () => {
    if (!searchTerm.trim()) return;
    
    setIsSearching(true);
    const results = await searchActivities(searchTerm, {
      type: filters.type !== 'all' ? filters.type : undefined,
      minCost: filters.minPrice > 0 ? filters.minPrice : undefined,
      maxCost: filters.maxPrice < 1000 ? filters.maxPrice : undefined,
    });
    
    setSearchResults(results);
    setIsSearching(false);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
  };

  // Handle save/unsave
  const handleToggleSave = async (activity: Activity) => {
    if (activity.isSaved) {
      const success = await unsaveActivity(activity.id);
      if (success) {
        setSearchResults(prev => 
          prev.map(a => a.id === activity.id ? { ...a, isSaved: false } : a)
        );
        onSuccess('Activity removed from saved activities');
      }
    } else {
      const activityWithTrip = {
        ...activity,
        tripId: trip.id
      };
      const success = await saveActivity(activityWithTrip);
      if (success) {
        setSearchResults(prev => 
          prev.map(a => a.id === activity.id ? { ...a, isSaved: true } : a)
        );
        onSuccess('Activity saved successfully');
      }
    }
  };

  // Open activity details
  const handleOpenDetails = (activity: Activity) => {
    setSelectedActivity(activity);
    setDetailDialogOpen(true);
  };

  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      type: 'all',
      minPrice: 0,
      maxPrice: 1000,
      rating: 0,
    });
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Search Bar */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Search Activities for {trip.destination}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            placeholder={`Search activities in ${trip.destination}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={isSearching || !searchTerm.trim()}
          >
            {isSearching ? <CircularProgress size={24} /> : 'Search'}
          </Button>
          
          <Button
            variant="outlined"
            color={showFilters ? 'primary' : 'inherit'}
            onClick={() => setShowFilters(!showFilters)}
            startIcon={<FilterIcon />}
          >
            Filters
          </Button>
        </Box>
      </Box>
      
      {/* Filters */}
      {showFilters && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Filter Options
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Activity Type</InputLabel>
                <Select
                  value={filters.type}
                  onChange={(e) => setFilters({ ...filters, type: e.target.value as string })}
                  label="Activity Type"
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="activity">Activities</MenuItem>
                  <MenuItem value="food">Food & Dining</MenuItem>
                  <MenuItem value="accommodation">Accommodation</MenuItem>
                  <MenuItem value="transportation">Transportation</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Min Price</InputLabel>
                <Select
                  value={filters.minPrice}
                  onChange={(e) => setFilters({ ...filters, minPrice: e.target.value as number })}
                  label="Min Price"
                >
                  <MenuItem value={0}>No Minimum</MenuItem>
                  <MenuItem value={25}>$25</MenuItem>
                  <MenuItem value={50}>$50</MenuItem>
                  <MenuItem value={100}>$100</MenuItem>
                  <MenuItem value={200}>$200</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Max Price</InputLabel>
                <Select
                  value={filters.maxPrice}
                  onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value as number })}
                  label="Max Price"
                >
                  <MenuItem value={50}>$50</MenuItem>
                  <MenuItem value={100}>$100</MenuItem>
                  <MenuItem value={200}>$200</MenuItem>
                  <MenuItem value={500}>$500</MenuItem>
                  <MenuItem value={1000}>$1000+</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Min Rating</InputLabel>
                <Select
                  value={filters.rating}
                  onChange={(e) => setFilters({ ...filters, rating: e.target.value as number })}
                  label="Min Rating"
                >
                  <MenuItem value={0}>Any Rating</MenuItem>
                  <MenuItem value={3}>3+ Stars</MenuItem>
                  <MenuItem value={4}>4+ Stars</MenuItem>
                  <MenuItem value={4.5}>4.5+ Stars</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={handleResetFilters} sx={{ mr: 1 }}>
              Reset Filters
            </Button>
            <Button 
              variant="contained" 
              onClick={handleSearch}
              disabled={isSearching || !searchTerm.trim()}
            >
              Apply Filters
            </Button>
          </Box>
        </Paper>
      )}
      
      {/* Search Results */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {isSearching ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : searchResults.length > 0 ? (
          <Grid container spacing={2}>
            {searchResults.map((activity) => (
              <Grid item xs={12} sm={6} md={4} key={activity.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  {activity.imageUrl && (
                    <CardMedia
                      component="img"
                      height="140"
                      image={activity.imageUrl}
                      alt={activity.title}
                    />
                  )}
                  
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography variant="h6" component="div" noWrap>
                        {activity.title}
                      </Typography>
                      <IconButton
                        color={activity.isSaved ? 'primary' : 'default'}
                        onClick={() => handleToggleSave(activity)}
                        size="small"
                      >
                        {activity.isSaved ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                      </IconButton>
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}>
                      {activity.description}
                    </Typography>
                    
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      {activity.rating && (
                        <Rating value={activity.rating} precision={0.5} size="small" readOnly />
                      )}
                      
                      {activity.cost !== undefined && (
                        <Typography variant="body2" color="text.secondary">
                          ${activity.cost.toFixed(2)}
                        </Typography>
                      )}
                    </Box>
                    
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      <Chip 
                        label={activity.type.charAt(0).toUpperCase() + activity.type.slice(1)} 
                        size="small" 
                        color="primary"
                        variant="outlined"
                      />
                      
                      {activity.duration && (
                        <Chip label={activity.duration} size="small" variant="outlined" />
                      )}
                    </Box>
                  </CardContent>
                  
                  <CardActions>
                    <Button size="small" onClick={() => handleOpenDetails(activity)}>
                      View Details
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : searchTerm ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1">
              No activities found. Try a different search term or filters.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1">
              Search for activities in {trip.destination} to get started.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Try searching for "tours", "restaurants", "museums", etc.
            </Typography>
          </Box>
        )}
      </Box>
      
      {/* Activity Detail Dialog */}
      {selectedActivity && (
        <ActivityDetailDialog
          open={detailDialogOpen}
          onClose={() => setDetailDialogOpen(false)}
          activity={selectedActivity}
          onSave={handleToggleSave}
          trip={trip}
        />
      )}
    </Box>
  );
};

export default ActivitySearch;